import { FormattedMessage, useIntl } from 'react-intl'
import { Button, Tooltip } from '@mui/material'
import { useState, useEffect, useRef } from 'react'
import CropImageDialog from 'components/dialog/CropImageDialog'
import imageCompression from 'browser-image-compression'
import { sendErrorNotification } from 'utils/notifications'
import FilePicker from '../FilePicker'
import { styled } from 'styled-components'
import { ChallengeImageType, type ChallengeImageType as ImageType } from 'api/types'
import { UploadCloudIcon } from 'assets/icons/upload-cloud'
import {
  confirmChallengeMediaUpload,
  deleteChallengeMedia,
  getChallengeMedia,
  requestChallengeMediaUpload,
} from 'store/challengesMedia/thunks'
import { useAppDispatch } from 'store'
import { resetChallengesMedia, selectChallengesMedia } from 'store/challengesMedia/slice'
import { useSelector } from 'react-redux'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import { RemoveIcon } from 'assets/icons/remove-icon'

type ImageField = {
  name: string
  label: string
  suggestedWidth: number
  suggestedHeight: number
  maxFileSizeMB: number
  circular?: boolean
  logoWidth?: number
}

const BACKGROUND_WIDTH = 1080
const BACKGROUND_HEIGHT = 827

interface Props {
  challenge: Challenge | null
  disabled: boolean
}

export default function ChallengeFormPhase4({ challenge, disabled }: Props): JSX.Element {
  const intl = useIntl()

  const dispatch = useAppDispatch()

  const [cropDialogOpen, setCropDialogOpen] = useState(false)
  const [imageInputData, setImageInputData] = useState('')
  const [currentImageField, setCurrentImageField] = useState<ImageField | null>(null)
  const [fileExtension, setFileExtension] = useState('')
  const { media: processedImages, loading: processedImagesLoading } = useSelector(selectChallengesMedia)
  const [localPreviews, setLocalPreviews] = useState<Record<string, string>>({})
  const [editingPreview, setEditingPreview] = useState<ImageField | null>(null)

  const allImages = { ...processedImages, ...localPreviews }
  const loadedForChallengeId = useRef<string | null>(null)
  const isUnsavedChallenge = challenge?.challengeId === '-1'

  useEffect(() => {
    setLocalPreviews({})
    dispatch(resetChallengesMedia())
  }, [])

  useEffect(() => {
    setLocalPreviews({})
    if (!challenge?.challengeId || challenge.challengeId === '-1') {
      return
    }

    if (loadedForChallengeId.current === challenge.challengeId) {
      return
    }
    loadedForChallengeId.current = challenge.challengeId

    const loadImages = async () => {
      setLocalPreviews({})
      if (!challenge?.challengeId || challenge.challengeId === '-1') {
        return
      }
      const imageTypes = [
        ChallengeImageType.LOGO,
        ChallengeImageType.BACKGROUND,
        ChallengeImageType.COVER,
        ChallengeImageType.BANNER,
        ChallengeImageType.HIGHLIGHT,
      ]
      const previews: Record<string, string> = {}

      for (const imageType of imageTypes) {
        try {
          await dispatch(
            getChallengeMedia({
              challengeId: challenge.challengeId,
              challengeImageType: imageType,
            }),
          ).unwrap()
        } catch {
          console.error(`Failed to load ${imageType} for challenge ${challenge.challengeId}`)
        }
      }

      if (Object.keys(previews).length > 0) {
        setLocalPreviews((prev) => ({ ...previews, ...prev }))
      }
    }

    loadImages()
  }, [challenge?.challengeId, dispatch])

  const imageFields: ImageField[] = [
    {
      name: ChallengeImageType.LOGO,
      label: intl.formatMessage({ id: 'challenges.challengeLogo' }),
      suggestedWidth: 330,
      suggestedHeight: 330,
      maxFileSizeMB: 0.5,
      circular: true,
    },
    {
      name: ChallengeImageType.BACKGROUND,
      label: intl.formatMessage({ id: 'challenges.backgroundImage' }),
      suggestedWidth: BACKGROUND_WIDTH,
      suggestedHeight: BACKGROUND_HEIGHT,
      maxFileSizeMB: 4.0,
    },
  ]

  const previewImageFields: ImageField[] = [
    {
      name: ChallengeImageType.COVER,
      label: intl.formatMessage({ id: 'challenges.challengeLanding' }),
      suggestedWidth: 375,
      suggestedHeight: 287,
      maxFileSizeMB: 0.5,
      logoWidth: 110,
    },
    {
      name: ChallengeImageType.HIGHLIGHT,
      label: intl.formatMessage({ id: 'challenges.joinChallengePanel' }),
      suggestedWidth: 343,
      suggestedHeight: 200,
      maxFileSizeMB: 0.5,
      logoWidth: 110,
    },
    {
      name: ChallengeImageType.BANNER,
      label: intl.formatMessage({ id: 'challenges.challengeCard' }),
      suggestedWidth: 343,
      suggestedHeight: 128,
      maxFileSizeMB: 0.5,
      logoWidth: 80,
    },
  ]

  // Create a center crop from an image
  const createCenterCrop = async (imageUrl: string, targetWidth: number, targetHeight: number): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')!

        // Generate @3x resolution for high-DPI screens
        canvas.width = targetWidth * 3
        canvas.height = targetHeight * 3

        const imgAspect = img.width / img.height
        const targetAspect = targetWidth / targetHeight

        let sourceX = 0
        let sourceY = 0
        let sourceWidth = img.width
        let sourceHeight = img.height

        if (imgAspect > targetAspect) {
          // Image is wider, crop sides
          sourceWidth = img.height * targetAspect
          sourceX = (img.width - sourceWidth) / 2
        } else {
          // Image is taller, crop top/bottom
          sourceHeight = img.width / targetAspect
          sourceY = (img.height - sourceHeight) / 2
        }

        ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, targetWidth * 3, targetHeight * 3)

        resolve(canvas.toDataURL('image/jpeg', 0.9))
      }
      img.src = imageUrl
    })
  }

  const saveImageToS3 = async (fieldName: ImageType, blob: Blob) => {
    if (!challenge?.challengeId || challenge?.challengeId === '-1') {
      sendErrorNotification('Challenge ID is missing. Please save the challenge before uploading images.')
      return
    }

    const { data } = await dispatch(
      requestChallengeMediaUpload({
        challengeId: challenge.challengeId,
        challengeImageType: fieldName,
      }),
    ).unwrap()

    if (!data || !data.uploadUrl || !data.uploadHeaders) {
      throw new Error(`Failed to get upload URL for ${fieldName}`)
    }

    const uploadResponse = await fetch(data.uploadUrl, {
      method: 'PUT',
      body: blob,
      headers: {
        ...data.uploadHeaders,
      },
    })

    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload image to S3 for ${fieldName}`)
    }

    const localUrl = URL.createObjectURL(blob)
    setLocalPreviews((prev) => ({ ...prev, [fieldName]: localUrl }))

    await dispatch(
      confirmChallengeMediaUpload({
        challengeId: challenge.challengeId,
        challengeImageType: fieldName,
        waitForCompletion: true,
      }),
    )

    await dispatch(
      getChallengeMedia({
        challengeId: challenge.challengeId,
        challengeImageType: fieldName,
      }),
    ).unwrap()
  }

  // The previews are cropped once and the very same blobs are both shown and uploaded, so that
  // they appear as a set as soon as they exist instead of one per finished upload
  const generateAndSavePreviewsFromBackground = async (backgroundImageUrl: string) => {
    const previews: { field: ImageField; blob: Blob }[] = []

    for (const field of previewImageFields) {
      try {
        const previewUrl = await createCenterCrop(backgroundImageUrl, field.suggestedWidth, field.suggestedHeight)
        const previewResponse = await fetch(previewUrl)
        previews.push({ field, blob: await previewResponse.blob() })
      } catch (error) {
        console.error(`Error generating preview image for ${field.name}`, error)
      }
    }

    setLocalPreviews((prev) => ({
      ...prev,
      ...Object.fromEntries(previews.map(({ field, blob }) => [field.name, URL.createObjectURL(blob)])),
    }))

    for (const { field, blob } of previews) {
      try {
        await saveImageToS3(field.name as ImageType, blob)
      } catch (error) {
        console.error(`Error saving preview image for ${field.name}`, error)
      }
    }
  }

  const onFilePickerChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    const imageField = imageFields.find((field) => field.name === e.target.name)

    if (!imageField) {
      return
    }

    const { files } = e.target

    if (files && files.length > 0) {
      const file = files[0]

      // File type validation
      if (!file.type.match(/image\/(jpeg|jpg|png)/)) {
        sendErrorNotification('Invalid image file type. Has to be JPEG, JPG or PNG.')
        return
      }

      setFileExtension(file.type.replace(/(.*)\//g, ''))

      if (!challenge?.challengeId || challenge?.challengeId === '-1') {
        sendErrorNotification('Challenge ID is missing. Please save the challenge before uploading images.')
        return
      }

      // Every branding image is cropped before it is uploaded, the background included: the
      // previews are derived from it, so it has to be framed before they are generated
      setCurrentImageField(imageField)
      setCropDialogOpen(true)

      const reader = new FileReader()
      reader.addEventListener('load', () => {
        setImageInputData(reader.result as string)
      })
      reader.readAsDataURL(file)
    }
  }

  const resizeImage = async (imageBlob: Blob) => {
    const options = {
      maxSizeMB: currentImageField!.maxFileSizeMB,
    }
    try {
      const file = new File([imageBlob], 'compressed.' + fileExtension, { type: imageBlob.type })
      const compressedFile = await imageCompression(file, options)
      return compressedFile
    } catch (error) {
      console.error(error)
    }
    return false
  }

  const handleCropResult = async (imageBlob?: Blob) => {
    setCropDialogOpen(false)
    setImageInputData('')

    if (!imageBlob) {
      setCurrentImageField(null)
      setEditingPreview(null)
      return
    }

    const fieldName = (editingPreview?.name || currentImageField?.name) as ImageType
    if (!fieldName) {
      return
    }

    try {
      // Compress picked files, but not preview edits (already compressed via background)
      const finalBlob = currentImageField ? await resizeImage(imageBlob) : imageBlob

      if (!finalBlob) {
        sendErrorNotification('Error compressing image')
        return
      }

      if (!challenge?.challengeId || challenge?.challengeId === '-1') {
        sendErrorNotification('Challenge ID is missing. Please save the challenge before uploading images.')
        return
      }

      await saveImageToS3(fieldName, finalBlob)

      // The three app previews are crops of the background, so a new background replaces them
      if (fieldName === ChallengeImageType.BACKGROUND) {
        await generateAndSavePreviewsFromBackground(URL.createObjectURL(finalBlob))
      }
    } catch (error) {
      sendErrorNotification('Error saving image')
      console.error(error)
    }

    setCurrentImageField(null)
    setEditingPreview(null)
  }

  const handleRemoveImage = async (fieldName: string) => {
    if (fieldName === undefined) {
      sendErrorNotification('Image field not filled.')
      return
    }
    if (!challenge?.challengeId || challenge?.challengeId === '-1') {
      sendErrorNotification('Challenge ID is missing. Please save the challenge before removing images.')
      return
    }

    const challengeId = challenge.challengeId

    const previewTypes = [ChallengeImageType.COVER, ChallengeImageType.BANNER, ChallengeImageType.HIGHLIGHT]

    // Removing background should also remove all preview crops
    if (fieldName === ChallengeImageType.BACKGROUND) {
      setLocalPreviews((prev) => {
        const updated = { ...prev }
        delete updated[fieldName]
        previewTypes.forEach((type) => delete updated[type])
        return updated
      })

      const previewTypesMap = previewTypes.map(async (type) => {
        await dispatch(
          deleteChallengeMedia({
            challengeId,
            challengeImageType: type,
          }),
        )
      })
      await Promise.all(previewTypesMap).catch((error) => {
        console.error('Error deleting preview images:', error)
      })
    } else if (fieldName !== ChallengeImageType.BACKGROUND) {
      setLocalPreviews((prev) => {
        const updated = { ...prev }
        delete updated[fieldName]
        return updated
      })
    }

    if (!fieldName) {
      return
    }
    dispatch(
      deleteChallengeMedia({
        challengeId,
        challengeImageType: fieldName as ImageType,
      }),
    )
  }

  const handleEditPreview = async (imageField: ImageField) => {
    if (!challenge?.challengeId) {
      sendErrorNotification('Please upload a background image first')
      return
    }

    // Use the preview's own image if available, otherwise fall back to background
    const previewUrl = allImages[imageField.name]
    const backgroundUrl = allImages[ChallengeImageType.BACKGROUND]
    const sourceUrl = previewUrl || backgroundUrl

    if (!sourceUrl) {
      sendErrorNotification('Please upload a background image first')
      return
    }

    setImageInputData(sourceUrl)
    setEditingPreview(imageField)
    setCropDialogOpen(true)
  }

  const renderImagePreview = (imageField: ImageField) => {
    const imageUrl = allImages[imageField.name]
    return (
      <ImagePreview
        $hasImage={!!imageUrl}
        $circular={imageField.circular}
        $aspectRatio={imageField.suggestedWidth / imageField.suggestedHeight}
      >
        {imageUrl ? (
          <>
            <ImageInner $circular={imageField.circular}>
              <img src={imageUrl} />
            </ImageInner>
            {!disabled && (
              <RemoveButton onClick={() => handleRemoveImage(imageField.name)}>
                <RemoveIcon width={24} height={24} fill="#0BB163" crossFill="white" />
              </RemoveButton>
            )}
          </>
        ) : (
          intl.formatMessage({ id: 'challenges.preview' }) || 'Preview'
        )}
      </ImagePreview>
    )
  }

  if (processedImagesLoading && Object.keys(allImages).length === 0) {
    return (
      <SpinnerContainer>
        <LoadingSpinnerGreen size={72} />
      </SpinnerContainer>
    )
  }

  return (
    <>
      <ContentContainer>
        <SectionHeading>
          <FormattedMessage id="challenges.brandingHeader" />
        </SectionHeading>
        <BrandingRow>
          {imageFields.map((imageField) => (
            <BrandingColumn key={imageField.name} $wide={!imageField.circular}>
              {renderImagePreview(imageField)}
              <FieldInfo>
                <FieldLabel>{imageField.label}</FieldLabel>
                <FieldDescription>
                  <FormattedMessage id="branding.fileTypes" />: JPG, JPEG, PNG
                  <br />
                  <FormattedMessage id="branding.recommendedDimensions" />: {imageField.suggestedWidth}x
                  {imageField.suggestedHeight} px
                </FieldDescription>
              </FieldInfo>
              <Tooltip
                title={isUnsavedChallenge ? 'Save as draft before uploading images' : ''}
                disableHoverListener={!isUnsavedChallenge}
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: '#222A35',
                      color: '#FFF',
                      fontSize: '16px',
                      padding: '20px',
                      fontWeight: '400',
                    },
                  },
                  arrow: {
                    sx: {
                      color: '#222A35',
                    },
                  },
                }}
              >
                <span>
                  <FilePicker
                    name={imageField.name}
                    id={imageField.name}
                    onChange={onFilePickerChange}
                    accept="image/jpeg, image/jpg, image/png"
                    disabled={disabled || challenge?.challengeId === '-1'}
                  >
                    <Button
                      disabled={disabled || challenge?.challengeId === '-1'}
                      variant="outlined"
                      component="span"
                      sx={{
                        padding: '10px 16px',
                        background: 'white',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        borderRadius: '12px',
                        border: '1px solid var(--neutral-300, #d4d4d4)',
                        color: 'var(--neutral-700, #404040)',
                        fontFamily: 'var(--family-sans, Söhne)',
                        fontSize: '14px',
                        fontWeight: 500,
                        lineHeight: '20px',
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: 'var(--neutral-300, #d4d4d4)',
                          background: 'white',
                        },
                      }}
                    >
                      <FormattedMessage id="strings.uploadFile" />
                      <UploadCloudIcon style={{ marginLeft: 8 }} />
                    </Button>
                  </FilePicker>
                </span>
              </Tooltip>
            </BrandingColumn>
          ))}
        </BrandingRow>

        <PreviewsSection>
          <SectionHeading>
            <FormattedMessage id="challenges.previews" />
          </SectionHeading>
          <PreviewsRow>
            {previewImageFields.map((imageField) => {
              const previewUrl = allImages[imageField.name] || allImages[ChallengeImageType.BACKGROUND]
              return (
                <PreviewCard key={imageField.name}>
                  <PreviewBox $aspectRatio={BACKGROUND_WIDTH / BACKGROUND_HEIGHT}>
                    <PreviewImageArea
                      $hasImage={!!previewUrl}
                      $aspectRatio={imageField.suggestedWidth / imageField.suggestedHeight}
                    >
                      {previewUrl && <PreviewImage src={previewUrl} />}
                      <PreviewLogo
                        $hasLogo={!!allImages[ChallengeImageType.LOGO]}
                        $ratio={(imageField.logoWidth || 80) / imageField.suggestedWidth}
                        $cover={imageField.name === ChallengeImageType.COVER}
                      >
                        {allImages[ChallengeImageType.LOGO] && <img src={allImages[ChallengeImageType.LOGO]} />}
                      </PreviewLogo>
                    </PreviewImageArea>

                    <EditButton
                      color="primary"
                      variant="contained"
                      onClick={() => handleEditPreview(imageField)}
                      disabled={!allImages[ChallengeImageType.BACKGROUND] || disabled}
                    >
                      <FormattedMessage id="strings.edit" />
                    </EditButton>
                  </PreviewBox>
                  <PreviewLabel>{imageField.label}</PreviewLabel>
                </PreviewCard>
              )
            })}
          </PreviewsRow>
        </PreviewsSection>
      </ContentContainer>
      <CropImageDialog
        open={cropDialogOpen}
        src={imageInputData}
        onClose={handleCropResult}
        aspect={
          editingPreview
            ? editingPreview.suggestedWidth / editingPreview.suggestedHeight
            : currentImageField
              ? currentImageField.suggestedWidth / currentImageField.suggestedHeight
              : 1
        }
        outputMaxWidth={
          editingPreview?.suggestedWidth || currentImageField?.suggestedWidth
            ? (editingPreview?.suggestedWidth || currentImageField?.suggestedWidth)! * 3
            : undefined
        }
        round={currentImageField?.circular}
        overlayImage={editingPreview ? allImages[ChallengeImageType.LOGO] : undefined}
        overlayRatio={editingPreview ? (editingPreview.logoWidth || 80) / editingPreview.suggestedWidth : undefined}
        imageFieldName={editingPreview?.name}
      />
    </>
  )
}

const ContentContainer = styled.div`
  width: 100%;
  background: white;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 48px;
  padding: 24px 24px 24px;
  box-sizing: border-box;
`

const SectionHeading = styled.h2`
  align-self: stretch;
  margin: 0;
  color: var(--foreground, #022314);
  font-family: var(--family-sans, Söhne);
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
`

const BrandingRow = styled.div`
  align-self: stretch;
  display: flex;
  align-items: flex-start;
  gap: 24px;
  flex-wrap: wrap;
  padding-top: 16px;
`

const BrandingColumn = styled.div<{ $wide?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  width: ${(props) => (props.$wide ? '100%' : '375px')};
  max-width: ${(props) => (props.$wide ? '535px' : '375px')};
  flex: ${(props) => (props.$wide ? '1 1 320px' : '0 0 auto')};
`

const FieldInfo = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FieldLabel = styled.div`
  color: var(--foreground, #022314);
  font-family: var(--family-sans, Söhne);
  font-size: 18px;
  font-weight: 700;
  line-height: 28px;
`

const FieldDescription = styled.p`
  align-self: stretch;
  margin: 0;
  color: var(--muted-foreground, #8a8a8e);
  font-family: var(--family-sans, Söhne);
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
`

const ImagePreview = styled.div<{ $hasImage: boolean; $circular?: boolean; $aspectRatio: number }>`
  border: 1.5px dashed ${(props) => (props.$hasImage ? '#373D40' : '#BBBBBD')};
  border-radius: ${(props) => (props.$circular ? '50%' : '12px')};
  height: 180px;
  width: ${(props) => (props.$circular ? '180px' : '100%')};
  max-width: ${(props) => (props.$circular ? '180px' : '100%')};
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  box-sizing: border-box;
  color: var(--muted-foreground, #8a8a8e);
  font-family: var(--family-sans, Söhne);
  font-weight: 500;
  font-size: 14px;
  aspect-ratio: ${(props) => (props.$circular ? '1' : props.$aspectRatio)};
`

const ImageInner = styled.div<{ $circular?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  background: white;
  width: ${(props) => (props.$circular ? '100%' : 'calc(100% - 8px)')};
  height: ${(props) => (props.$circular ? '100%' : 'calc(100% - 8px)')};
  border-radius: ${(props) => (props.$circular ? '50%' : '8px')};

  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const RemoveButton = styled.div`
  position: absolute;
  top: -10px;
  right: -10px;
  cursor: pointer;
`

const PreviewsSection = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
`

const PreviewsRow = styled.div`
  align-self: stretch;
  display: flex;
  align-items: flex-start;
  gap: 24px;
  flex-wrap: wrap;
`

const PreviewCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 375px;
  max-width: 100%;
`

const PreviewLabel = styled.div`
  color: #373d40;
  font-family: var(--family-sans, Söhne);
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
`

const PreviewBox = styled.div<{ $aspectRatio: number }>`
  background-color: #f5f5f9;
  width: 100%;
  aspect-ratio: ${(props) => props.$aspectRatio};
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`

// Fills the background width while keeping the preview's own aspect ratio, so it matches the crop
const PreviewImageArea = styled.div<{ $hasImage: boolean; $aspectRatio: number }>`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  max-height: 100%;
  aspect-ratio: ${(props) => props.$aspectRatio};
  border: ${(props) => (props.$hasImage ? 'none' : '1.5px dashed #BBBBBD')};
`

const PreviewImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

// Same placement as the overlay in CropImageDialog: sized relative to the preview width, centred,
// or half-overlapping the bottom edge on the landing cover
const PreviewLogo = styled.div<{ $hasLogo: boolean; $ratio: number; $cover: boolean }>`
  position: absolute;
  left: 50%;
  ${(props) => (props.$cover ? 'bottom: 0;' : 'top: 50%;')}
  transform: translate(-50%, ${(props) => (props.$cover ? '50%' : '-50%')});
  width: ${(props) => props.$ratio * 100}%;
  aspect-ratio: 1;
  box-sizing: border-box;
  border-radius: 50%;
  border: ${(props) => (props.$hasLogo ? 'none' : '1.5px dashed #BBBBBD')};
  overflow: hidden;
  ${(props) => props.$cover && 'clip-path: inset(0 0 50% 0);'}

  & img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const EditButton = styled(Button)`
  position: absolute;
  top: 16px;
  right: 16px;
  border-radius: 999px;
  text-transform: none;
  min-width: unset;
  padding: 8px 24px;
  line-height: 1;
  background: var(--primary, #0bb163);

  &.Mui-disabled {
    color: ${(props) => props.theme.customPalette.light};
    opacity: 0.5;
  }
`

const SpinnerContainer = styled.div`
  display: flex;
  width: 100%;
  margin: 50px auto;
  justify-content: center;
`
